import React from 'react';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import FAQ from '../components/ui/FAQ';
import { mailValidatorFAQs } from '../data/faqs';
import Icon from '../components/AppIcon';

const Product = () => {
  // Enhanced SEO Data
  const seoData = {
    title: 'OctalChip Products - Email Validator SaaS | Professional Email Validation Service',
    description: 'OctalChip offers professional email validation SaaS with 99.9% accuracy. Validate single emails or bulk lists to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.',
    keywords: 'OctalChip, email validator, email validation service, bulk email validation, email verification, SaaS email tool, professional email validation, bounce rate reduction, email deliverability, tech company',
    canonical: 'https://www.octalchip.com/product',
    tags: ['OctalChip', 'email validator', 'SaaS', 'email validation', 'email verification', 'bulk validation', 'tech company'],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'Products', url: 'https://www.octalchip.com/product' }
    ],
    faqData: mailValidatorFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "OctalChip",
      "description": "OctalChip is a technology company offering professional SaaS solutions including Email Validator SaaS with 99.9% accuracy",
      "url": "https://www.octalchip.com",
      "logo": "https://www.octalchip.com/octalchiplogo.svg",
      "sameAs": [
        "https://www.octalchip.com"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "OctalChip Products",
        "itemListElement": [
          {
            "@type": "SoftwareApplication",
            "name": "Email Validator SaaS",
            "description": "Professional email validation service with 99.9% accuracy for single and bulk email validation",
            "url": "https://www.octalchip.com/product",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "description": "100 free credits with company domain signup"
            }
          }
        ]
      }
    }
  };

  const heroStatistics = [
    { value: '99.9%', label: 'Accuracy Rate' },
    { value: '100', label: 'Free Credits' },
    { value: 'Bulk', label: 'Processing' },
    { value: 'API', label: 'Integration' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        ogImage={seoData.ogImage}
        tags={seoData.tags}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Email Validator SaaS
            </h1>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-6 sm:mb-8"></div>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 leading-relaxed mb-6 sm:mb-8 px-4">
              Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists 
              to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12">
              {heroStatistics.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-xl sm:text-2xl md:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">{stat.value}</div>
                  <div className="text-xs sm:text-sm md:text-base text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Email Validator SaaS Section */}
      <section id="features" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Professional Email Validation</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Validate emails with 99.9% accuracy to improve your deliverability and reduce bounce rates
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-[#00d690]/5 to-blue-500/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-12 items-center">
                <div>
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Mail" size={20} className="sm:w-6 sm:h-6 text-[#00d690]" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Email Validator SaaS</h3>
                      <p className="text-xs sm:text-sm text-[#00d690] font-semibold">Professional Email Validation Service</p>
                    </div>
                  </div>
                  
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                    Professional email validation service with 99.9% accuracy. Validate single emails or bulk lists 
                    to reduce bounce rates and improve deliverability. Get 100 free credits with company domain signup.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
                    <div className="text-center bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00d690] mb-1">99.9%</div>
                      <div className="text-xs sm:text-sm text-gray-600">Accuracy Rate</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00d690] mb-1">Bulk</div>
                      <div className="text-xs sm:text-sm text-gray-600">Processing</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00d690] mb-1">API</div>
                      <div className="text-xs sm:text-sm text-gray-600">Integration</div>
                    </div>
                    <div className="text-center bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                      <div className="text-lg sm:text-xl md:text-2xl font-bold text-[#00d690] mb-1">GDPR</div>
                      <div className="text-xs sm:text-sm text-gray-600">Compliant</div>
                    </div>
                  </div>
                  
                  <div className="mb-4 sm:mb-6">
                    <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 sm:mb-3">Key Features:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Icon name="Check" size={14} className="sm:w-4 sm:h-4 text-[#00d690] mr-2 flex-shrink-0" />
                        Real-time validation
                      </div>
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Icon name="Check" size={14} className="sm:w-4 sm:h-4 text-[#00d690] mr-2 flex-shrink-0" />
                        Bulk processing
                      </div>
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Icon name="Check" size={14} className="sm:w-4 sm:h-4 text-[#00d690] mr-2 flex-shrink-0" />
                        Detailed analytics
                      </div>
                      <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Icon name="Check" size={14} className="sm:w-4 sm:h-4 text-[#00d690] mr-2 flex-shrink-0" />
                        API access
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#00d690] hover:bg-[#00b876] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors text-center"
                    >
                      Try Email Validator
                    </a>
                    <a
                      href="/contact"
                      className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors text-center"
                    >
                      Get Custom Quote
                    </a>
                  </div>
            </div>
            
                <div className="text-center mt-6 lg:mt-0">
                  <div className="bg-white rounded-xl p-4 sm:p-6 md:p-8 shadow-lg border-2 border-dashed border-[#00d690]/30">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                      <Icon name="Mail" size={32} className="sm:w-10 sm:h-10 text-[#00d690]" />
                    </div>
                    <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4">Free Trial Available</h4>
                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                      Get 100 free credits to test our email validation service
                    </p>
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="text-gray-600">Free Credits:</span>
                        <span className="font-semibold text-[#00d690]">100</span>
                      </div>
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="text-gray-600">Validation Type:</span>
                        <span className="font-semibold text-gray-800">Single & Bulk</span>
                      </div>
                      <div className="flex justify-between items-center text-xs sm:text-sm">
                        <span className="text-gray-600">Support:</span>
                        <span className="font-semibold text-gray-800">Email Support</span>
                      </div>
                    </div>
                    <a
                      href="https://mailvalidator.octalchip.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block w-full bg-[#00d690] hover:bg-[#00b876] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors mt-4 sm:mt-6"
                    >
                      Start Free Trial
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6">
              Ready to Improve Your Email Deliverability?
            </h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 px-4">
              Start validating your emails today with our professional service. Get 100 free credits 
              and experience the difference in your email marketing campaigns.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <a
                href="https://mailvalidator.octalchip.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#00d690] hover:bg-[#00b876] text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors"
              >
                Start Free Trial
              </a>
              <a
                href="/contact"
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={mailValidatorFAQs}
        title="Email Validator FAQs"
        description="Common questions about our email validation service, accuracy, pricing, and integration."
      />

      <Footer />
    </div>
  );
};

export default Product; 